import { AggregateValidationError, ValidationResult, Validator } from '../public'
import { ValidatorOrShape } from '../validators/types'

/**
 * Checks if a value is a validator function.
 * @param func - The value to check
 * @returns True if the value is a validator function
 */
export function isValidator(func: unknown): func is Validator {
  return typeof func === 'function'
}

/**
 * Options for shape validation.
 */
export interface ValidateShapeOptions {
  /** If true, validation errors are returned instead of thrown */
  soft?: boolean
}

/**
 * Interface for the validateShape function
 */
export interface ValidateShapeFunction {
  (
    obj: unknown,
    shape: ValidatorOrShape,
    options?: ValidateShapeOptions
  ): { success: boolean, results: ValidationResult[] }
}

/**
 * Recursively validates an object against a shape definition.
 * 
 * @param obj - The object to validate
 * @param shape - The shape to validate against
 * @param options - Optional validation options
 * @returns An object containing validation results
 * @throws {AggregateValidationError} If validation fails and soft mode is not enabled
 * 
 * @remarks
 * - If the shape is a validator, it will be used to validate the entire object
 * - If the shape is an object, it will be used to validate each property recursively
 * - For arrays, each element is validated against the shape
 * - Validation errors are collected and either returned or thrown based on the soft option
 */
export function validateShape(
  obj: unknown,
  shape: ValidatorOrShape,
  options?: ValidateShapeOptions
): { success: boolean, results: ValidationResult[] } {
  if (isValidator(shape)) {
    const results = shape(obj)
    const success = results.every(r => r.valid)
    return { success, results }
  }

  if (typeof obj !== 'object' || obj === null) {
    throw new Error('Expected an object or array for validation')
  }

  if (Array.isArray(obj)) {
    const arrayResults = obj.flatMap((item, index) => {
      const { results } = validateShape(item, shape, { soft: true })
      return results.map(res => ({
        ...res,
        field: `[${index}]${res.field ? '.' + res.field : ''}`
      }))
    })
    const success = arrayResults.every(r => r.valid)
    return { success, results: arrayResults }
  }
  const validations = collectValidations(obj, shape)

  const results: ValidationResult[] = []

  for (const { validator, field, value } of validations) {
    try {
      const validationResults = validator(value)
      for (const res of validationResults) {
        results.push({ ...res, field, value })
      }
    } catch (err) {
      if (err instanceof AggregateValidationError) {
        for (const e of err.errors) {
          results.push({ field: e.field, valid: false, value: e.value, message: e.message })
        }
      } else {
        throw err
      }
    }
  }

  const success = results.every(r => r.valid)

  if (success || options?.soft) {
    return { success, results: results.map(r => ({ ...r, field: r.field, value: r.value })) }
  }

  const errors = results
    .filter(r => !r.valid)
    .map(r => ({ field: r.field, value: r.value, message: r.message }))

  throw new AggregateValidationError(errors)
}

/**
 * Collects all validations that need to be performed on an object.
 * @internal
 * 
 * @param obj - The object to collect validations for
 * @param shape - The shape to validate against
 * @param prefix - Optional prefix for field names
 * @returns Array of validations to perform
 */
function collectValidations(
  obj: unknown,
  shape: { [key: string]: unknown },
  prefix = ''
): Array<{ validator: Validator, field: string, value: unknown }> {
  const collected: Array<{ validator: Validator, field: string, value: unknown }> = []

  if (typeof obj !== 'object' || obj === null) {
    return collected
  }

  const record = obj as Record<string, unknown>

  for (const key in shape) {
    const validatorOrNestedShape = shape[key]
    const fieldPath = prefix ? `${prefix}.${key}` : key
    const value = record[key]

    if (isValidator(validatorOrNestedShape)) {
      collected.push({ validator: validatorOrNestedShape, field: fieldPath, value })
    } else if (typeof validatorOrNestedShape === 'object' && validatorOrNestedShape !== null) {
      collected.push(...collectValidations(value, validatorOrNestedShape as Record<string, Validator>, fieldPath))
    }
  }

  return collected
}